import { UserRole } from '@barrikadave/shared';
import { USER_FIELDS } from './constants';
import type { ColumnMapping, MappedUser, RowValidation, UserField } from './types';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const VALID_ROLES = Object.values(UserRole);

export function autoDetectMappings(headers: string[]): ColumnMapping {
  const mapping: ColumnMapping = {};
  const usedFields = new Set<UserField>();

  for (const header of headers) {
    const normalized = header.toLowerCase().trim();
    let matched: UserField | null = null;

    for (const field of USER_FIELDS) {
      if (usedFields.has(field.key)) continue;
      if (field.aliases.some((alias) => alias === normalized)) {
        matched = field.key;
        break;
      }
    }

    if (matched) {
      mapping[header] = matched;
      usedFields.add(matched);
    } else {
      mapping[header] = null;
    }
  }

  return mapping;
}

export function applyMappings(
  rows: string[][],
  mapping: ColumnMapping,
  headers: string[],
): MappedUser[] {
  return rows.map((row, rowIndex) => {
    const user: MappedUser = {
      rowIndex,
      email: '',
      firstName: '',
      lastName: '',
    };

    headers.forEach((header, colIndex) => {
      const field = mapping[header];
      if (!field || colIndex >= row.length) return;
      const value = row[colIndex]?.trim() || '';

      switch (field) {
        case 'email':
          user.email = value;
          break;
        case 'firstName':
          user.firstName = value;
          break;
        case 'lastName':
          user.lastName = value;
          break;
        case 'password':
          user.password = value || undefined;
          break;
        case 'role':
          user.role = value || undefined;
          break;
        case 'phone':
          user.phone = value || undefined;
          break;
        case 'position':
          user.position = value || undefined;
          break;
      }
    });

    return user;
  });
}

export function validateMappedUsers(users: MappedUser[]): RowValidation[] {
  const seenEmails = new Set<string>();

  return users.map((user) => {
    const errors: string[] = [];
    const warnings: string[] = [];

    if (!user.email) {
      errors.push('Email mungon');
    } else if (!EMAIL_REGEX.test(user.email)) {
      errors.push('Email i pavlefshem');
    } else if (seenEmails.has(user.email.toLowerCase())) {
      errors.push('Email i dyfishte ne CSV');
    } else {
      seenEmails.add(user.email.toLowerCase());
    }

    if (!user.firstName || user.firstName.length < 2) {
      errors.push('Emri duhet te kete te pakten 2 karaktere');
    }

    if (!user.lastName || user.lastName.length < 2) {
      errors.push('Mbiemri duhet te kete te pakten 2 karaktere');
    }

    if (user.role) {
      const normalizedRole = user.role.toUpperCase();
      if (!VALID_ROLES.includes(normalizedRole as UserRole)) {
        errors.push(`Roli "${user.role}" nuk eshte i vlefshem (${VALID_ROLES.join(', ')})`);
      }
    }

    if (user.password && user.password.length < 8) {
      errors.push('Fjalekalimi duhet te kete te pakten 8 karaktere');
    }

    if (!user.password) {
      warnings.push('Fjalekalimi mungon, do te gjenerohet automatikisht');
    }

    return { rowIndex: user.rowIndex, errors, warnings };
  });
}

export function hasRequiredMappings(mapping: ColumnMapping): boolean {
  const mappedFields = new Set(Object.values(mapping).filter(Boolean));
  return (
    mappedFields.has('email') && mappedFields.has('firstName') && mappedFields.has('lastName')
  );
}

export function getMappingStats(validations: RowValidation[]) {
  const totalErrors = validations.reduce((sum, v) => sum + v.errors.length, 0);
  const totalWarnings = validations.reduce((sum, v) => sum + v.warnings.length, 0);
  const rowsWithErrors = validations.filter((v) => v.errors.length > 0).length;
  return { totalErrors, totalWarnings, rowsWithErrors };
}
